// @flow
import * as React from "react";
import createHistory from "history/createBrowserHistory";
import createHashHistory from "history/createHashHistory";
import createMemoryHistory from "history/createMemoryHistory";
import createQueryHistory from './createQueryHistory';
import {render} from "react-dom";
import {observe} from "mobx";
import {reisy, inject} from "reisy";
import initStyle from "../styling/index.js";
import {isIOS} from '../environment.js'

import "../styling/static.css";

import App from "./app.js";
import Player, {isEmbedded} from "./player.js";
import type {Options, State, Location} from "./player.js";

const HISTORIES = {
	browser: createHistory,
	hash: createHashHistory,
	query: createQueryHistory,
	default: createMemoryHistory,
};

/**
 * This is the client entry point and renders the Player into the given element.
 * @param {State} state - The player state.
 * @param {Options} options - The player options.
 * @param {Element} elem - The target element to render the player into.
 * @param {Location=} _location - If set, overrides the history location
 */
export async function renderClient(state: State, options: Options, elem: Element, _location?: Location) {
	let whichHistory = "browser";
	let param, historyBaseName;
	if (document.location.protocol === 'file:' || options.forceHashHistory) {
		whichHistory = "hash";
	}
	if (options.history) {
		whichHistory = options.history.type || options.history;
		param = options.history.param;
		historyBaseName = options.history.basename;

		if (historyBaseName) {
			options.historyBaseName = historyBaseName;
		}

		// FIXME: this should only be temporaray until we can change the embed code of the respective clients
		if (window.location.hostname.match(/^((www\.)?grazer\.at|(www\.)?ruefa\.at)/) && window.location.search.startsWith('?path=') && whichHistory === 'hash') {
			window.location.replace(window.location.href.split('?')[0] + '#!' + window.location.search.replace('?path=', '').replace('&', '?'));
			return;
			//whichHistory = 'query';
		}
	}
	if (whichHistory === 'browser' && window.location.hash.startsWith('#!/')) {
		const parts = window.location.href.split("#!");
		const r = parts[0].replace(/\/+$/, "") + "/" + parts[1].replace(/^\/+/, "");
		window.location.replace(r);
	}
	if (whichHistory === 'hash') { 
		// If there is a base in the document head but no hash in the url, setting a hash can trigger a reload.
		// To prevent this we need to remove the base, set a hash and put the base back in.
		const base = document.head.querySelector('base'); 
		if (!window.location.hash && base) {
			document.head.removeChild(base); 
			window.location.hash = '#!/'; 
			document.head.appendChild(base);
		}

		if(~window.location.search.indexOf('_escaped_fragment_=')) {
			window.location.hash = '#!' + decodeURIComponent(window.location.search.split('_escaped_fragment_=')[1]);
		}
	}
	if (param !== undefined) {
		options.history = {
			type: whichHistory,
			param,
		}
	} else {
		options.history = whichHistory;
	}
	const history = (HISTORIES[whichHistory] || HISTORIES.default)({hashType: 'hashbang', param, basename: historyBaseName});
	let navigateTo = (to, replace) => history[replace ? "replace" : "push"](to);

	const viewport = {
		width: window.innerWidth,
		height: window.innerHeight,
	};
	var resizeTimeout;
	const postSize = () => {
		clearTimeout(resizeTimeout);
		if (isEmbedded) {
			resizeTimeout = setTimeout(() => {
				window.parent.postMessage({type: "kent-size", w: elem.offsetWidth, h: elem.offsetHeight}, "*");
			}, 100);
		}
	};
	window.addEventListener("resize", () => {
		viewport.width = window.innerWidth;
		viewport.height = window.innerHeight;
		postSize();
	});

	const location = _location || history.location;
	if (isEmbedded(location)) {
		options.isEmbedded = true;
	}

	//prevent zoom gesture on iOS because disabling it in meta viewport is ignored
	if (isIOS) {
		document.body.addEventListener('gesturestart', function(e) {
			e.preventDefault();
		});
	}

	if (options.jackInTheBox && !options.stylePrefix) {
		let currentElem = elem;
		let parents = ['.pagestrip-root'];
		const idTest = /^[A-Za-z]+[\w\-\:\.]*$/;
		do {
			if (currentElem.id && idTest.test(currentElem.id)) {
				parents.unshift('#' + currentElem.id);
			}
			if (currentElem === document.body) { break; }
			currentElem = currentElem.parentNode;
		} while (currentElem.parentNode);

		options.stylePrefix = parents.join(' ');
	}
	if (options.stylePrefix) {
		reisy.prefix(options.stylePrefix);
	}

	const player = new Player(options, state, {viewport, navigateTo});

	observe(player, 'element', () => {
		postSize();
	});

	if (!player.options.alwaysLoadKioskData) {
		inject();
	}

	await player.handleNavigation(location);

	if (player.options.alwaysLoadKioskData) {
		//override messages
		const appearance = player.content && (player.content.appearance || player.content.publisher && player.content.publisher.appearance) || 
			player.publisher && player.publisher.appearance || 
			{};
		const {messages} = appearance;
		if (messages) {
			player.options.messages = messages;
		}

		//override styles
		// $FlowFixMe: because we don’t care
		initStyle(
			Object.assign({},
				options,
				appearance
			));
		inject();
	}

	render(<App player={player} />, elem);
	history.listen((location, action) => {
		player.handleNavigation(location, action.toLowerCase());
	});

	// maybe inject feed links
	if (player.options.jackInTheBox && player.publisher && player.options.history !== "memory") {
		let rssUrl = `${player.options.apiBase}feeds/publisher-${player.publisher.id}/rss/`;
		let atomUrl = `${player.options.apiBase}feeds/publisher-${player.publisher.id}/atom/`;

		if (!player.publisher.canonicalBaseUrl && "number" === typeof player.options.context) {
			let extra = `?language=${player.options.locale}&size=300&base=`;

			switch (player.options.history) {
				case "hash": {
					if (typeof window === "undefined") {
						extra = null; break;
					}

					const idx = window.location.href.indexOf("#!");
					if (idx < 0) {
						extra = null; break;
					}

					// cut off all url parameters from the base url as well, as a heuristic,
					// in case links are coming with tracking parameters and the such...
					extra += encodeURIComponent(window.location.href.substring(0, idx).replace(/\?.*$/, "") + "#!");
					break;
				}

				case "browser": {
					if (
						!player.options.historyBaseName ||
						typeof window === "undefined"
					) {
						extra = null; break;
					}

					let base = player.options.historyBaseName;
					if (base.endsWith("/")) { base = base.substring(0, base.length-1); }
					extra += encodeURIComponent(`${window.location.origin}${base}`);
					break;
				}
			}

			if (!extra) {
				rssUrl = atomUrl = null;
			} else {
				rssUrl += extra; atomUrl += extra;
			}
		} else if (!player.publisher.canonicalBaseUrl && "number" !== typeof player.options.context) {
			// we only inject feeds when we don't have an explicit canonical if the embedded
			// context is a whole publisher, not just an individual story.
			rssUrl = atomUrl = null;
		}

		if (rssUrl) {
			let rssLink = document.head.querySelector(`link[href="${rssUrl}"]`);
			if (!rssLink) {
				rssLink = document.createElement('link');
				rssLink.setAttribute('rel', 'alternate');
				rssLink.setAttribute('type', 'application/rss+xml');
				rssLink.setAttribute('href', rssUrl);
				rssLink.setAttribute('title', `RSS feed - ${player.publisher.name}`);
				document.head.appendChild(rssLink);
			}
		}

		if (atomUrl) {
			let atomLink = document.head.querySelector(`link[href="${atomUrl}"]`);
			if (!atomLink) {
				atomLink = document.createElement('link');
				atomLink.setAttribute('rel', 'alternate');
				atomLink.setAttribute('type', 'application/atom+xml');
				atomLink.setAttribute('href', atomUrl);
				atomLink.setAttribute('title', `Atom feed - ${player.publisher.name}`);
				document.head.appendChild(atomLink);
			}
		}
	}

	window.addEventListener("message", ({data}) => {
		if (data.type === "kent-navigate") {
			if(data.action === 'push') {
				navigateTo(data.url);
			} else {
				player.handleNavigation(data.url, "pop");
			}
		} else if(data.type === 'kent-top-href') {
			player.options.topHref = data.href;
		}
	});
}



// WEBPACK FOOTER //
// ./lib/player/client.js